import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Terminal, Brain, Gauge, CheckCircle, XCircle } from "@phosphor-icons/react";
import { KnowledgeGraph } from "./KnowledgeGraph";
import { PHASES, methodColor } from "@/lib/ui";

export const LiveExecution = ({ run }) => {
  const logRef = useRef(null);
  const logs = run.logs || [];
  const results = run.results || [];
  const phase = PHASES[run.status] || PHASES.queued;
  const passed = results.filter((r) => r.status === "pass").length;
  const failed = results.filter((r) => r.status === "fail").length;

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs.length]);

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">
          <span className="h-2 w-2 rounded-full bg-primary pulse-dot" /> {phase.label}
        </div>
        <h2 className="font-head text-3xl font-bold tracking-tight">{run.target_title || run.name}</h2>
        <p className="mt-1 truncate font-mono text-sm text-muted-foreground">{run.source || run.base_url}</p>
      </div>

      {/* Progress bar */}
      <div className="mb-6 overflow-hidden rounded-full bg-muted">
        <motion.div
          className="h-2 rounded-full bg-primary"
          animate={{ width: `${run.progress || 0}%` }}
          transition={{ ease: "easeOut", duration: 0.5 }}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Terminal — hero */}
        <div className="tracing-beam rounded-2xl bg-card lg:col-span-2">
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <Terminal size={18} weight="bold" className="text-primary" />
            <span className="font-head text-sm font-semibold">Agent activity</span>
            <div className="ml-auto flex gap-1.5">
              <span className="h-3 w-3 rounded-full bg-[hsl(0_75%_65%)]" />
              <span className="h-3 w-3 rounded-full bg-[hsl(38_85%_55%)]" />
              <span className="h-3 w-3 rounded-full bg-[hsl(160_70%_45%)]" />
            </div>
          </div>
          <div ref={logRef} data-testid="agent-logs"
            className="h-[380px] overflow-y-auto p-5 font-mono text-xs leading-relaxed">
            {logs.length === 0 && <div className="text-muted-foreground">Booting agent…</div>}
            {logs.map((l, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                className="flex gap-2 py-0.5">
                <span className="shrink-0 text-muted-foreground/50">{new Date(l.time).toLocaleTimeString()}</span>
                <span className={
                  l.level === "error" ? "text-[hsl(0_75%_60%)]"
                  : l.level === "success" ? "text-[hsl(160_70%_45%)]"
                  : "text-foreground/90"}>
                  {l.message}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-rows-2 gap-5">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Gauge size={18} weight="bold" className="text-primary" />
              <span className="font-head text-sm font-semibold">Live metrics</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Metric icon={CheckCircle} label="Passed" value={passed} color="hsl(160 70% 42%)" />
              <Metric icon={XCircle} label="Failed" value={failed} color="hsl(0 75% 60%)" />
            </div>
            <div className="mt-4 border-t border-border pt-4">
              <div className="text-xs text-muted-foreground">Endpoints discovered</div>
              <div className="font-head text-2xl font-bold">{run.endpoints?.length || 0}</div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-3 flex items-center gap-2">
              <Brain size={18} weight="bold" className="text-primary" />
              <span className="font-head text-sm font-semibold">Recent checks</span>
            </div>
            <div className="space-y-1.5 overflow-y-auto max-h-[150px]">
              {results.slice(-6).reverse().map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {r.status === "pass"
                    ? <CheckCircle size={14} weight="fill" className="shrink-0 text-[hsl(160_70%_42%)]" />
                    : <XCircle size={14} weight="fill" className="shrink-0 text-[hsl(0_75%_60%)]" />}
                  <span className={`font-mono font-semibold ${methodColor(r.method)}`}>{r.method}</span>
                  <span className="truncate font-mono text-muted-foreground">{r.path}</span>
                </div>
              ))}
              {results.length === 0 && <div className="text-xs text-muted-foreground">No checks yet.</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge graph */}
      <div className="relative mt-5 overflow-hidden rounded-2xl border border-border bg-card">
        <div className="absolute left-5 top-4 z-10 flex items-center gap-2">
          <Brain size={18} weight="bold" className="text-primary" />
          <span className="font-head text-sm font-semibold">Learned knowledge graph</span>
        </div>
        <div className="h-[420px] p-4 pt-2">
          <KnowledgeGraph graph={run.graph} activeIndex={results.length - 1} />
        </div>
      </div>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color }) => (
  <div>
    <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon size={14} weight="fill" style={{ color }} /> {label}
    </div>
    <div className="font-head text-3xl font-bold" style={{ color }}>{value}</div>
  </div>
);
